/**
 * Svelte 5 Store für Webtrainer-NG.
 *
 * Reactive State via Runes ($state, $derived).
 * Persistierung in localStorage via lib/store/persistence.
 */
import { type FsrsCard, isDue, newCard, review } from '$lib/fsrs';
import type { Card, Rating } from '$types/card';
import {
  deserialize,
  load,
  migrate,
  type PersistedFilterState,
  type PersistedState,
  type ReviewLogEntry,
  save,
  serialize,
} from './persistence';

interface InternalState {
  cards: Record<string, FsrsCard>;
  filter: PersistedFilterState;
  reviewLog: ReviewLogEntry[];
}

function persistedToInternal(p: PersistedState): InternalState {
  const cards: Record<string, FsrsCard> = {};
  for (const [id, s] of Object.entries(p.cards)) cards[id] = deserialize(s);
  return { cards, filter: p.filter, reviewLog: p.reviewLog };
}

function internalToPersisted(s: InternalState): PersistedState {
  const cards: Record<string, ReturnType<typeof serialize>> = {};
  for (const [id, c] of Object.entries(s.cards)) cards[id] = serialize(c);
  return { version: 2, cards, filter: s.filter, reviewLog: s.reviewLog };
}

import { matchesBurningTopic } from '$lib/filter';
/**
 * Plain class — KEINE Svelte-Runes hier (sonst Test-Run außerhalb Svelte-Compiler scheitert).
 * Reactivity in App.svelte: nach store.applyReview() / store.setFilter() wird refreshDue() gerufen,
 * das die lokalen $state-Vars in App.svelte triggert.
 */
import { BURNING_TOPICS } from '$types/burning-topics';

export class WebtrainerStore {
  private internal: InternalState = {
    cards: {},
    filter: {
      burningTopics: [],
      decks: [],
      searchQuery: '',
      cardTypes: [],
      sources: [],
      tags: [],
      mode: 'review',
    },
    reviewLog: [],
  };

  /** Snapshot für genau EINEN Undo-Schritt (Fehltipp auf dem Handy). */
  private lastReview: { cardId: string; prevState: FsrsCard | null } | null = null;

  /** Optionales Storage (Tests injizieren MockStorage). */
  constructor(private storage: Storage | null = null) {
    if (storage) {
      this.internal = persistedToInternal(load(storage));
    }
  }

  /**
   * Liefert FSRS-State für eine Karte; erstellt einen neuen wenn unbekannt.
   * `now` wird als initial-due für neue Karten verwendet — wichtig für
   * Race-freie filteredDueIds() (sonst due=now+ε > now → faelschlich nicht due).
   */
  getCardState(cardId: string, now: Date = new Date()): FsrsCard {
    let st = this.internal.cards[cardId];
    if (!st) {
      st = newCard(now);
      this.internal.cards[cardId] = st;
    }
    return st;
  }

  /** Wendet ein Review an, persistiert Resultat + Log-Entry. */
  applyReview(cardId: string, rating: Rating, now = new Date()): FsrsCard {
    // Undo-Snapshot VOR getCardState — das legt für unbekannte Karten
    // bereits einen neuen State an; prevState=null heißt "war neu".
    const existed = this.internal.cards[cardId];
    this.lastReview = { cardId, prevState: existed ? { ...existed } : null };
    const before = this.getCardState(cardId, now);
    const result = review(before, rating, now);
    this.internal.cards[cardId] = result.card;
    this.internal.reviewLog.push({
      cardId,
      rating,
      reviewedAt: now.toISOString(),
      dueAfter: result.card.due.toISOString(),
      stability: result.stability,
      difficulty: result.difficulty,
    });
    this.persist();
    return result.card;
  }

  filteredDueIds(allCards: readonly Card[], now = new Date()): string[] {
    const visible = this.applyFilter(allCards);
    if (this.internal.filter.mode === 'all') return visible.map((c) => c.id);
    if (this.internal.filter.mode === 'study') return visible.map((c) => c.id);
    return visible.filter((c) => isDue(this.getCardState(c.id, now), now)).map((c) => c.id);
  }

  applyFilter(allCards: readonly Card[]): Card[] {
    const f = this.internal.filter;
    const q = f.searchQuery.trim().toLowerCase();
    const activeTopics = f.burningTopics.length
      ? BURNING_TOPICS.filter((t) => f.burningTopics.includes(t.id))
      : null;
    return allCards.filter((c) => {
      if (activeTopics && !activeTopics.some((t) => matchesBurningTopic(c, t))) return false;
      if (f.decks.length && !f.decks.includes(c.deck)) return false;
      if (f.cardTypes.length && !f.cardTypes.includes(c.type)) return false;
      if (f.sources.length && !(c.source && f.sources.some((s) => c.source?.includes(s))))
        return false;
      if (f.tags.length && !f.tags.some((t) => c.tags.includes(t))) return false;
      if (q) {
        const hay = [c.id, ...c.tags, c.deck, ...cardTextFields(c)].join('\n').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }

  setFilter(partial: Partial<PersistedFilterState>): void {
    this.internal.filter = { ...this.internal.filter, ...partial };
    this.persist();
  }

  get filter(): PersistedFilterState {
    return this.internal.filter;
  }

  get reviewLog(): readonly ReviewLogEntry[] {
    return this.internal.reviewLog;
  }

  /** Export zur Weitergabe an tools/fsrs_optimize.py. */
  exportReviewLog(): ReviewLogEntry[] {
    return [...this.internal.reviewLog];
  }

  get canUndo(): boolean {
    return this.lastReview !== null;
  }

  /** Macht genau das letzte Review rückgängig; gibt die Karten-ID zurück. */
  undoLastReview(): string | null {
    if (!this.lastReview) return null;
    const { cardId, prevState } = this.lastReview;
    if (prevState) this.internal.cards[cardId] = prevState;
    else delete this.internal.cards[cardId];
    this.internal.reviewLog.pop();
    this.lastReview = null;
    this.persist();
    return cardId;
  }

  /** Vollständiges Backup (FSRS-State + Filter + Review-Log) als JSON-String. */
  exportBackup(): string {
    return JSON.stringify(internalToPersisted(this.internal), null, 2);
  }

  /**
   * Ersetzt den kompletten State durch ein Backup (Export-Format oder v1).
   * Wirft bei kaputtem JSON oder leerem Ergebnis — der State bleibt dann unangetastet.
   */
  importBackup(json: string): { cards: number; reviews: number } {
    const migrated = migrate(JSON.parse(json));
    const cards = Object.keys(migrated.cards).length;
    const reviews = migrated.reviewLog.length;
    if (cards === 0 && reviews === 0) {
      throw new Error('Backup enthält keine Karten oder Reviews — Import abgebrochen.');
    }
    this.internal = persistedToInternal(migrated);
    this.lastReview = null;
    this.persist();
    return { cards, reviews };
  }

  /** Löscht ALLES — für Tests / Reset. */
  reset(): void {
    this.internal = {
      cards: {},
      filter: this.internal.filter,
      reviewLog: [],
    };
    this.persist();
  }

  private persist(): void {
    if (!this.storage) return;
    save(internalToPersisted(this.internal), this.storage);
  }
}

function cardTextFields(c: Card): string[] {
  const fields: string[] = [];
  if ('text' in c && c.text) fields.push(c.text);
  if ('front' in c && c.front) fields.push(c.front);
  if ('back' in c && c.back) fields.push(c.back);
  return fields;
}

export type { PersistedFilterState, PersistedState, ReviewLogEntry } from './persistence';
export { defaultFilter, load, STORAGE_KEY, save } from './persistence';
